/**
 * UUID Validation Utility
 * 
 * Use these functions for:
 * - Pre-validation in forms before submission
 * - Client-side validation feedback
 * - Logging/debugging purposes
 * - Critical operations requiring UUID format verification
 */

/**
 * Validates if a string is a valid UUID v4
 * @param {string} uuid - The UUID string to validate
 * @returns {boolean} - True if valid UUID, false otherwise
 */
export function isValidUUID(uuid) {
  if (!uuid || typeof uuid !== 'string') {
    return false;
  }
  
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex?.test(uuid);
}

/**
 * Validates UUID and throws error with user-friendly message if invalid
 * Use for critical operations where UUID format must be verified before database queries
 * 
 * @param {string} uuid - The UUID to validate
 * @param {string} fieldName - Name of the field for error message (e.g., 'ID client', 'ID envoi')
 * @throws {Error} - Throws error with clear message if UUID is invalid
 */
export function validateUUIDOrThrow(uuid, fieldName = 'ID') {
  if (!isValidUUID(uuid)) {
    throw new Error(
      `${fieldName} invalide. Format attendu : xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx. Valeur reçue : ${uuid}`
    );
  }
}

/**
 * Validates UUID and returns error message if invalid
 * Useful for form validation feedback
 * 
 * @param {string} uuid - The UUID to validate
 * @param {string} fieldName - Name of the field for error message
 * @returns {string|null} - Error message if invalid, null if valid
 */
export function validateUUIDMessage(uuid, fieldName = 'ID') {
  if (!isValidUUID(uuid)) {
    return `${fieldName} invalide. Format attendu : xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx`;
  }
  return null;
}

/**
 * Safely validates UUID before database operations
 * Returns null if invalid instead of throwing
 * 
 * @param {string} uuid - The UUID to validate
 * @returns {string|null} - The UUID if valid, null if invalid
 */
export function safeUUID(uuid) {
  return isValidUUID(uuid) ? uuid : null;
}

export default {
  isValidUUID,
  validateUUIDOrThrow,
  validateUUIDMessage,
  safeUUID
};